import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select';
import { FaTimes, FaExchangeAlt, FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import apiRequest from '../../services/apiService';
import { toast } from 'react-toastify';

const TransferOrderModal = ({ 
    isOpen, 
    onClose, 
    order, 
    branches, 
    currentBranchId,
    onTransfer 
}) => {
    const [targetBranch, setTargetBranch] = useState(null);
    const [transferReason, setTransferReason] = useState('');
    const [isTransferring, setIsTransferring] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);

    // Fetch and filter branches
    useEffect(() => {
        if (isOpen) {
            fetchBranches();
            // Reset state when modal opens
            setTargetBranch(null);
            setTransferReason('');
            setValidationResult(null);
        }
    }, [isOpen]);

    const fetchBranches = async () => {
        setLoadingBranches(true);
        try {
            console.log('Fetching branches for transfer. Current branch ID:', currentBranchId);
            // Use the transfer-specific endpoint that returns all branches regardless of user role
            const data = await apiRequest('/clients/getBranchesForTransfer', 'POST', {});
            
            if (data.success && data.data) {
                console.log('Fetched branches for transfer:', data.data);
                console.log('Current branch ID to filter:', currentBranchId);
                // Filter out current branch AND master/warehouse accounts (only show regular branches)
                const filtered = data.data
                    .filter(branch => {
                        const isCurrentBranch = branch._id === currentBranchId;
                        const isMasterAccount = branch.masterAccount === true;
                        const shouldInclude = !isCurrentBranch && !isMasterAccount;
                        console.log(`Branch ${branch.branchName} (${branch._id}): shouldInclude=${shouldInclude}, isCurrent=${isCurrentBranch}, isMaster=${isMasterAccount}`);
                        return shouldInclude;
                    })
                    .map(branch => ({
                        value: branch._id,
                        label: branch.branchName
                    }));
                
                console.log('Filtered branches for transfer:', filtered);
                setAvailableBranches(filtered);
            } else {
                console.warn('No branches data received or request failed:', data);
            }
        } catch (error) {
            console.error('Error fetching branches:', error);
            setAvailableBranches([]);
        } finally {
            setLoadingBranches(false);
        }
    };

    useEffect(() => {
        // Validate transfer when target branch changes
        if (targetBranch && order) {
            validateTransfer();
        } else {
            setValidationResult(null);
        }
    }, [targetBranch, order]);

    const validateTransfer = async () => {
        if (!targetBranch || !order) return;
        
        setIsValidating(true);
        try {
            const data = await apiRequest('/orders/validateOrderTransfer', 'POST', {
                orderId: order._id,
                sourceBranchId: currentBranchId,
                targetBranchId: targetBranch.value,
                products: order.productsInfo
            });

            setValidationResult(data);
        } catch (error) {
            console.error('Validation error:', error);
            setValidationResult({
                success: false,
                canTransfer: false,
                message: 'Failed to validate transfer. Please try again.',
                errors: ['Network error or server unavailable']
            });
        } finally {
            setIsValidating(false);
        }
    };

    const handleTransfer = async () => {
        if (!targetBranch || !transferReason.trim()) {
            toast.error('Please select a target branch and provide a reason for transfer.');
            return;
        }

        if (validationResult && !validationResult.canTransfer) {
            toast.error('Transfer validation failed. Please check the issues listed.');
            return;
        }

        setIsTransferring(true);
        try {
            const data = await apiRequest('/orders/transferOrder', 'POST', {
                orderId: order._id,
                sourceBranchId: currentBranchId,
                targetBranchId: targetBranch.value,
                transferReason: transferReason,
                products: order.productsInfo
            });
            
            if (data.success) {
                onTransfer(data);
                onClose();
            } else {
                toast.error(data.message || 'Transfer failed. Please try again.');
            }
        } catch (error) {
            console.error('Transfer error:', error);
            toast.error('Failed to transfer order. Please try again.');
        } finally {
            setIsTransferring(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <FaExchangeAlt className="text-2xl" />
                                <h2 className="text-2xl font-bold">Transfer Order</h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                            >
                                <FaTimes className="text-xl" />
                            </button>
                        </div>
                        <p className="mt-2 text-blue-100">Order ID: {order?.saleId || 'N/A'}</p>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {/* Order Summary */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-gray-800 mb-3">Order Summary</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Items:</span>
                                    <span className="font-medium">{order?.itemsCount || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Amount:</span>
                                    <span className="font-medium">₹{order?.totalAmount || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Current Status:</span>
                                    <span className={`font-medium ${
                                        order?.status === 'Delivered' ? 'text-green-600' :
                                        order?.status === 'Cancelled' ? 'text-red-600' :
                                        'text-blue-600'
                                    }`}>
                                        {order?.status || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Target Branch Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Select Target Branch *
                            </label>
                            <Select
                                options={availableBranches}
                                value={targetBranch}
                                onChange={setTargetBranch}
                                placeholder="Choose branch to transfer to..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isLoading={loadingBranches}
                                menuPortalTarget={document.body}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: '0.5rem',
                                        borderColor: '#d1d5db',
                                        '&:hover': {
                                            borderColor: '#3b82f6'
                                        }
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>

                        {/* Validation Results */}
                        {isValidating && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center space-x-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600"></div>
                                    <span className="text-blue-700 font-medium">Validating transfer...</span>
                                </div>
                            </div>
                        )}

                        {validationResult && !isValidating && (
                            <div className={`border rounded-lg p-4 ${
                                validationResult.canTransfer 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-red-50 border-red-200'
                            }`}>
                                <div className="flex items-start space-x-3">
                                    {validationResult.canTransfer ? (
                                        <FaCheckCircle className="text-green-600 text-xl mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <FaExclamationTriangle className="text-red-600 text-xl mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="flex-grow">
                                        <h4 className={`font-semibold mb-2 ${
                                            validationResult.canTransfer ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            {validationResult.canTransfer ? 'Transfer Validated' : 'Transfer Issues Found'}
                                        </h4>
                                        <p className={`text-sm mb-2 ${
                                            validationResult.canTransfer ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                            {validationResult.message}
                                        </p>

                                        {/* Product-level validation details */}
                                        {validationResult.productValidation && validationResult.productValidation.length > 0 && (
                                            <div className="mt-3 space-y-2">
                                                <p className="text-sm font-medium text-gray-700">Product Availability:</p>
                                                {validationResult.productValidation.map((product, idx) => (
                                                    <div key={idx} className={`text-sm p-2 rounded ${
                                                        product.available ? 'bg-green-100' : 'bg-red-100'
                                                    }`}>
                                                        <div className="flex items-start justify-between">
                                                            <span className="font-medium">{product.productName}</span>
                                                            {product.available ? (
                                                                <FaCheckCircle className="text-green-600 flex-shrink-0" />
                                                            ) : (
                                                                <FaExclamationTriangle className="text-red-600 flex-shrink-0" />
                                                            )}
                                                        </div>
                                                        <div className="text-xs mt-1 text-gray-600">
                                                            Weight: {typeof product.weight === 'object' ? product.weight.weightLabel : product.weight} | Required: {product.required} | 
                                                            Available: {product.available ? product.availableQuantity : 0}
                                                        </div>
                                                        {!product.available && product.reason && (
                                                            <div className="text-xs mt-1 text-red-600 font-medium">
                                                                {product.reason}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Error list */}
                                        {validationResult.errors && validationResult.errors.length > 0 && (
                                            <ul className="mt-2 text-sm space-y-1">
                                                {validationResult.errors.map((error, idx) => (
                                                    <li key={idx} className="flex items-start space-x-2">
                                                        <span className="text-red-600">•</span>
                                                        <span className="text-red-700">{error}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* Warnings */}
                                        {validationResult.warnings && validationResult.warnings.length > 0 && (
                                            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-2">
                                                <div className="flex items-start space-x-2">
                                                    <FaInfoCircle className="text-yellow-600 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="text-sm font-medium text-yellow-800">Warnings:</p>
                                                        <ul className="mt-1 text-sm space-y-1">
                                                            {validationResult.warnings.map((warning, idx) => (
                                                                <li key={idx} className="text-yellow-700">• {warning}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Transfer Reason */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Reason for Transfer *
                            </label>
                            <textarea
                                value={transferReason}
                                onChange={(e) => setTransferReason(e.target.value)}
                                placeholder="Explain why this order needs to be transferred (e.g., stock unavailable, closer to delivery location, operational requirements...)"
                                rows={4}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                maxLength={500}
                            />
                            <div className="text-right text-xs text-gray-500 mt-1">
                                {transferReason.length}/500 characters
                            </div>
                        </div>

                        {/* Important Notes */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                                <FaInfoCircle className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-amber-800 mb-2">Important Notes:</h4>
                                    <ul className="text-sm text-amber-700 space-y-1">
                                        <li>• Order ownership will be transferred to the selected branch</li>
                                        <li>• Inventory will be restored to current branch and deducted from target branch</li>
                                        <li>• Transfer history will be recorded with your reason</li>
                                        <li>• This action cannot be undone automatically</li>
                                        <li>• Customer will not be notified of the transfer</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex items-center justify-end space-x-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                            disabled={isTransferring}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleTransfer}
                            disabled={
                                !targetBranch || 
                                !transferReason.trim() || 
                                isTransferring || 
                                isValidating ||
                                (validationResult && !validationResult.canTransfer)
                            }
                            className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                                !targetBranch || 
                                !transferReason.trim() || 
                                isTransferring || 
                                isValidating ||
                                (validationResult && !validationResult.canTransfer)
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                            }`}
                        >
                            {isTransferring ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                    <span>Transferring...</span>
                                </>
                            ) : (
                                <>
                                    <FaExchangeAlt />
                                    <span>Transfer Order</span>
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default TransferOrderModal;
